import gql from 'graphql-tag';

export const officePromoCodesCountFragment = gql`
    fragment OfficePromoCodesCountFragment on OfficePromoCodesCountOutput {
        totalCount
        totalActive
    }
`;

export const organisationTariff = gql`
    fragment OrganisationTariffFragment on OrganisationTariffOutput {
        id
        tariffId
        validFrom
        validTo
        tariffStatus
        name
        price
        discount
        currency
        studio
        monthly
        stripePriceId
        active
        firstIsExpiringEarlyReminderSent
        secondIsExpiringEarlyReminderSent
    }
`;

export const organisationLabelFragment = gql`
    fragment LectureLabelOutputFragment on LectureLabelOutput {
        labelId
        organisationLabelId
        name
        color
    }
`;

export const licensePaymentMethodFragment = gql`
    fragment OrganisationLicensePaymentMethodOutputFragment on OrganisationLicensePaymentMethodOutput {
        id
        licensePaymentMethodId
        paymentMethod
        paymentMethodIdentifier
    }
`;

export const organisationTariffMeta = gql`
    fragment OrganisationTariffMetaFragment on OrganisationOfficeTariffMetaOutput {
        tariffStatus
        tariffName
        studio
    }
`;

export const officeOtherCurrency = gql`
    fragment OfficeOtherCurrencyOutputFragment on OfficeOtherCurrencyOutput {
        id
        currencyId
        officeId
        currencyCode
        bankAccountNumber
        isActive
    }
`;

export const officePaymentMethod = gql`
    fragment OfficePaymentMethodFragment on OfficePaymentMethodOutput {
        id
        officePaymentMethodId
        officeId
        longDescription
        shortDescription
        category
        type
        isActive
        isError
        isSystemMethod
        name
        stripeDeactivationDeadline
        stripeChargesEnabled
        stripePayoutsEnabled
        stripeDisabledReason
        isStripeAccountActive
        otherCurrency {
            ...OfficeOtherCurrencyOutputFragment
        }
    }
    ${officeOtherCurrency}
`;

export const instancePaymentMethod = gql`
    fragment InstancePaymentMethodFragment on InstancePaymentMethodOutput {
        id
        instancePaymentMethodId
        isActive
        lectureId
        lectureTemplateId
        officePaymentMethodId
        officeId
        officePaymentMethod {
            ...OfficePaymentMethodFragment
        }
    }
    ${officePaymentMethod}
`;

export const bookTariff = gql`
    fragment TariffOutputFragment on TariffOutput {
        id
        tariffId
        name
        price
        currency
        studio
        monthly
        active
        advanced
        stripePriceId
    }
`;

export const orderStorno = gql`
    fragment OrderStornoFragment on OrderStornoOutput {
        id
        orderPaymentStornoId
        date
        refundValue
        currency
        percentFee
        stornoStatus
        description
        organisationEmployeeId
        receiptId
    }
`;

export const user = gql`
    fragment UserFragment on UserOutput {
        id
        userId
        name
        description
        email
        phone
        firstname
        lastname
        requestedAccountType
        image
        registrationStatus
        systemStatus
        lastLogin
    }
`;

export const organisationInfo = gql`
    fragment OrganisationInfo on OrganisationOutput {
        id
        organisationId
        organisationType
        name
        registrationDate
    }
`;

export const organisationLicensePaymentAddress = gql`
    fragment OrganisationLicensePaymentAddressFragment on OrganisationLicensePaymentAddressOutput {
        id
        licensePaymentAddressId
        isCompany
        subjectName
        street
        city
        postalCode
        country
        companyIdentificationNumber
        vatNumber
        licensePaymentMethod
        isVATPayer
        isStripePayment
        stripePaymentMethodBrand
        stripePaymentMethodLast4
        stripePaymentMethodExpiration
        waitingForWebhook
    }
`;

export const organisationUser = gql`
    fragment OrganisationUserFragment on OrganisationUserOutput {
        id
        organisationUserId
        organisationId
        userId
        name
        description
        image
        instagram
        role
    }
`;

const publicOfficeTariffLimits = gql`
    fragment PublicOrganisationOfficeTariffLimitsFragment on PublicOrganisationOfficeTariffLimitsOutput {
        id
        organisationTariff {
            ...OrganisationTariffFragment
        }
        bookTariff {
            ...TariffOutputFragment
        }
        count_LECTURE
        count_COURSE
        count_WORKSHOP
        activeNotCanceledCourseIds
        activeNotCanceledWorkshopIds
        activeAllNotCanceledWorkshops
        activeAllNotCanceledCourses
        tariffMonthlyLimitValidFrom
        tariffMonthlyLimitValidTo
    }
    ${organisationTariff}
    ${bookTariff}
`;

export const officeTariffLimits = gql`
    fragment OrganisationOfficeTariffLimitsFragment on OrganisationOfficeTariffLimitsOutput {
        id
        organisationTariff {
            ...OrganisationTariffFragment
        }
        bookTariff {
            ...TariffOutputFragment
        }
        count_LECTURE
        count_COURSE
        count_WORKSHOP
        activeNotCanceledWorkshops {
            id
            workshopId
            name
            start
            end
            isCanceled
        }
        activeNotCanceledCourses {
            id
            lectureId
            lectureName
            latestEnd
        }
        activeAllNotCanceledWorkshops
        activeAllNotCanceledCourses
        tariffMonthlyLimitValidFrom
        tariffMonthlyLimitValidTo
    }
    ${organisationTariff}
    ${bookTariff}
`;

export const organisationOffice = gql`
    fragment OrganisationOfficeFragment on OrganisationOfficeOutput {
        id
        officeId
        organisationId
        name
        description
        domain
        image
        backgroundImage
        organisationId
        accountNumber
        iban
        bic
        phone
        email
        url
        instagram
        facebookUrl
        youtubeUrl
        default
        isHidden
        officeConfigId
        defaultCurrencyCode
        orgTariffMeta {
            ...OrganisationTariffMetaFragment
        }
        otherCurrencies {
            ...OfficeOtherCurrencyOutputFragment
        }
        tariffLimits {
            ...OrganisationOfficeTariffLimitsFragment
        }
        officePromoCodesCount {
            ...OfficePromoCodesCountFragment
        }
        isExpertAlaUltimate
    }
    ${organisationTariffMeta}
    ${officeOtherCurrency}
    ${officeTariffLimits}
    ${officePromoCodesCountFragment}
`;

export const organisationSmsPackagePayment = gql`
    fragment OrganisationSmsPackagePaymentFragment on OrganisationSmsPackagePaymentOutput {
        id
        smsPackagePaymentId
        dateCreated
        datePaid
        overdueDate
        paymentStatus
        paymentIdentifier
        paymentMethod
        discount
        price
        currency
        smsTariff
        organisationId
        publicHtmlUrl
        thePayPayUrl
        thePayDetailUrl
        paymentStatusMessage
    }
`;

export const organisationLicensePayment = gql`
    fragment OrganisationLicensePaymentFragment on OrganisationLicensePaymentOutput {
        id
        licensePaymentId
        dateCreated
        datePaid
        overdueDate
        paymentStatus
        paymentIdentifier
        paymentMethod
        discount
        price
        currency
        bookTariffId
        organisationId
        publicHtmlUrl
        thePayPayUrl
        thePayDetailUrl
        stripeClientSecret
        stripePaymentIntentId
        paymentStatusMessage
        stripePaymentSucceeded
        stripePaymentIntentInvoiceId
        stripeSubscriptionId
        organisationTariff {
            ...OrganisationTariffFragment
        }
    }
    ${organisationTariff}
`;

export const officePlace = gql`
    fragment OfficePlaceFragment on OfficePlaceOutput {
        id
        placeId
        officeId
        name
        photo
        description
        archived
        addressId
    }
`;

export const lecturePaymentOptions = gql`
    fragment LecturePaymentOptionFragment on PaymentOptionsOutput {
        id
        paymentOptionId
        option
    }
`;

export const address = gql`
    fragment AddressFragment on AddressOutput {
        id
        addressId
        street
        city
        postalCode
        country
    }
`;

export const officeClientBasic = gql`
    fragment OfficeClientBasicFragment on OfficeClientOutput {
        id
        clientId
        status
        gdprConsent
        marketingConsent
        registrationDate
        street
        city
        postalCode
        country
        clientBirthday
        clientBirthNumber
    }
`;

export const publicOffice = gql`
    fragment PublicOfficeFragment on PublicOfficeOutput {
        id
        officeId
        organisationId
        name
        image
        domain
        accountNumber
        iban
        bic
        phone
        email
        url
        instagram
        facebookUrl
        youtubeUrl
        stripeConnectAccountId
        timezone
        publicCalendarView
        otherCurrencies {
            ...OfficeOtherCurrencyOutputFragment
        }
        officeConfig {
            shouldIssueReceipts
            timezone
            alternatesAllowed
            closeLogoutBeforeAllowedWhenever
            closeLogoutBeforeHours
            penaltyZonesAllowed
            cashPaymentForReservationEnabled
            stripeConnectAccountId
            doNotShowAttendeesForLecturesAndCourses
            doNotShowAttendeesForCourses
            promoCodesVisibilityEnabled
            vouchersVisibilityEnabled
        }
        tariffLimits {
            ...PublicOrganisationOfficeTariffLimitsFragment
        }
        isExpertAlaUltimate
    }
    ${officeOtherCurrency}
    ${publicOfficeTariffLimits}
`;

export const officeClientUserWrapper = gql`
    fragment OfficeClientUserWrapperFragment on OfficeClientUserWrapperOutput {
        email
        client {
            ...OfficeClientBasicFragment
        }
        user {
            ...UserFragment
        }
    }
    ${officeClientBasic}
    ${user}
`;

export const clientVoucher = gql`
    fragment ClientVoucherFragment on ClientVoucherOutput {
        id
        clientVoucherId
        name
        value
        remainingValue
        currency
        validTo
        code
        claimedByUserId
        image
        office {
            officeId
            domain
            name
            timezone
            url
            image
        }
        order {
            id
            paymentStatus
        }
    }
`;

export const clientOrder = gql`
    fragment ClientOrderFragment on ClientOrderOutput {
        id
        clientOrderId
        date
        datePaid
        price
        fullPrice
        currency
        type
        paymentStatus
        paymentIdentifier
        paymentMethod
        toBankAccount
        toBankAccountIban
        toBankAccountBic
        usedMakeUpSessionValidTo
        receiptBucketKey
        receiptId
        variableSymbol
        stripePaymentIntentId
        stripePaymentStatus
        dynamicNote
        orderForName
        benefitOrOtherPaymentMethodCustomName
        workshopId
        courseId
        qrCode
        discountInfo
        invoiceDescription
        officeId
        clientVoucher {
            ...ClientVoucherFragment
        }
        orderStorno {
            ...OrderStornoFragment
        }
    }
    ${orderStorno}
    ${clientVoucher}
`;

export const alternateEntryBalance = gql`
    fragment AlternateEntryBalanceFragment on AlternateEntryOutput {
        id
        alternateEntryId
        validTo
        expiringEarlyReminderSent
        officeId
        organisationClientId
    }
`;

export const officeClientBookingStats = gql`
    fragment OfficeClientBookingStatsFragment on OfficeClientBookingStatsOutput {
        totalReservationCount
        totalPassedReservationsCount
        didNotArriveReservationsCount
        totalActiveReservationsCount
    }
`;

export const officeClientStats = gql`
    fragment OfficeClientStatsFragment on OfficeClientStatsOutput {
        id
        clientId
        bookingStats {
            ...OfficeClientBookingStatsFragment
        }
        numberOfCredits
        numberOfEntries
        numberOfReplacementEntries
        alternateEntry {
            ...AlternateEntryBalanceFragment
        }
    }
    ${officeClientBookingStats}
    ${alternateEntryBalance}
`;

export const officeClientStatsWithoutBookingStats = gql`
    fragment OfficeClientStatsWithoutBookingStatsFragment on OfficeClientStatsOutput {
        id
        clientId
        bookingStats {
            ...OfficeClientBookingStatsFragment
        }
        numberOfCredits
        numberOfEntries
        validToCredits
        validToPass
        numberOfReplacementEntries
        alternateEntry {
            ...AlternateEntryBalanceFragment
        }
    }
    ${officeClientBookingStats}
    ${alternateEntryBalance}
`;

export const creditBalance = gql`
    fragment CreditBalanceFragment on ClientBalanceCreditOutput {
        id
        creditBalanceId
        numberOfCredits
        validTo
        expiringEarlyReminderSent
    }
`;

export const passBalance = gql`
    fragment PassBalanceFragment on ClientBalancePassOutput {
        id
        passBalanceId
        numberOfEntries
        validTo
        expiringEarlyReminderSent
    }
`;

/**
 * preview for user
 */
export const userOrder = gql`
    fragment UserOrderFragment on ClientOrderOutput {
        ...ClientOrderFragment
        office {
            ...PublicOfficeFragment
        }
    }
    ${clientOrder}
    ${publicOffice}
`;

export const userOfficeClient = gql`
    fragment UserClientFragment on OfficeClientOutput {
        id
        clientId
        status
    }
`;

export const pricingOption = gql`
    fragment PricingOptionFragment on PricingOptionOutput {
        id
        pricingOptionId
        value
        currency
        type
        name
        description
        isSingleCourseSession
        isVisible
    }
`;

export const clientHistoryLog = gql`
    fragment ClientHistoryFragment on OfficeClientHistoryOutput {
        id
        clientHistoryId
        date
        text
        type
        organisationClientId
        data
    }
`;

export const clientNote = gql`
    fragment ClientNoteFragment on OfficeClientNotesOutput {
        id
        clientNoteId
        date
        note
        noteType
        organisationClientId
        organisationEmployeeId
    }
`;

export const clientLabel = gql`
    fragment ClientLabelFragment on OfficeClientLabelOutput {
        id
        clientLabelId
        name
        color
        organisationClientId
    }
`;

export const userReservations = gql`
    fragment UserReservationFragment on ReservationOutput {
        id
        reservationId
        reservationToken
        createTime
        reservationState
        lectureTypeOfClientOrderAttendance
        isPresent
        organisationClientId
        type
        lectureInstancePricingOption {
            ...PricingOptionFragment
        }
        clientOrder {
            ...ClientOrderFragment
        }
    }
    ${pricingOption}
    ${clientOrder}
`;

export const userReservationsPublic = gql`
    fragment UserReservationPublicFragment on ReservationOutput {
        id
        reservationId
        reservationToken
        reservationState
        lectureTypeOfClientOrderAttendance
        isPresent
        organisationClientId
        type
        clientOrder {
            paymentStatus
        }
    }
`;

export const clientOffice = gql`
    fragment ClientOfficeFragment on ClientOfficeOutput {
        id
        creditBalance {
            ...CreditBalanceFragment
        }
        passBalance {
            ...PassBalanceFragment
        }
        alternateEntry {
            ...AlternateEntryBalanceFragment
        }
    }
    ${creditBalance}
    ${passBalance}
    ${alternateEntryBalance}
`;

export const officeClient = gql`
    fragment OfficeClientFragment on OfficeClientOutput {
        ...OfficeClientBasicFragment
        user {
            id
            userId
            name
            email
            phone
            image
        }
        stats {
            ...OfficeClientStatsFragment
        }
        labels {
            ...ClientLabelFragment
        }
    }
    ${officeClientBasic}
    ${officeClientStats}
    ${clientLabel}
`;

export const officeClientTableGrid = gql`
    fragment OfficeClientTableGridFragment on OfficeClientOutput {
        ...OfficeClientBasicFragment
        user {
            id
            userId
            name
            email
            phone
            image
            registrationStatus
        }
        stats {
            ...OfficeClientStatsWithoutBookingStatsFragment
        }
        labels {
            ...ClientLabelFragment
        }
    }
    ${officeClientBasic}
    ${officeClientStatsWithoutBookingStats}
    ${clientLabel}
`;

/**
 * preview for office
 */
export const fullOfficeClientOrder = gql`
    fragment FullOfficeClientOrderFragment on ClientOrderOutput {
        ...ClientOrderFragment
        client {
            ...OfficeClientFragment
        }
    }
    ${clientOrder}
    ${officeClient}
`;

export const fullOfficeClient = gql`
    fragment FullOfficeClientFragment on OfficeClientOutput {
        ...OfficeClientBasicFragment
        user {
            id
            userId
            name
            email
            phone
            image
            registrationStatus
        }
        notes {
            ...ClientNoteFragment
        }
        labels {
            ...ClientLabelFragment
        }
        history {
            ...ClientHistoryFragment
        }
        stats {
            ...OfficeClientStatsFragment
        }
    }
    ${officeClientBasic}
    ${clientNote}
    ${clientLabel}
    ${clientHistoryLog}
    ${officeClientStats}
`;

export const officeClientWaitlistNotificationAttendee = gql`
    fragment OfficeClientWaitlistNotificationAttendeeFragment on OfficeClientWaitlistNotificationAttendeeOutput {
        id
        clientId
        status
        gdprConsent
        marketingConsent
        registrationDate
        street
        city
        postalCode
        country
        email
        user {
            id
            userId
            name
            email
            phone
            image
        }
    }
`;

export const officeTerms = gql`
    fragment OfficeTermsFragment on OfficeTermsOutput {
        id
        termsId
        officeId
        type
        fileUrl
        fileName
        linkUrl
        text
        tosLastUpdate
        gdprType
        gdprLinkUrl
        gdprText
        gdprLastUpdate
    }
`;

export const paymentProportionStatsOutputFragment = gql`
    fragment PaymentProportionStatsOutputFragment on PaymentProportionStatsOutput {
        paymentMethod
        count
    }
`;

export const valueCurrencyFragment = gql`
    fragment ValueCurrencyFragment on ValueCurrencyOutput {
        value
        currency
    }
`;

export const waitingPaymentValueCurrencyFragment = gql`
    fragment WaitingPaymentValueCurrencyFragment on WaitingPaymentValueCurrencyOutput {
        sumValue
        currency
        waitingPaymentsCount
    }
`;

export const statsRevenueFragment = gql`
    fragment StatsRevenueFragment on StatsRevenueOutput {
        currency
        paymentMethods {
            type
            value
        }
    }
`;

export const statsPenaltyStornoRevenueFragment = gql`
    fragment StatsPenaltyStornoRevenueFragment on StatsPenaltyStornoRevenueOutput {
        currency
        value
    }
`;

export const rowOfficeStatsFragment = gql`
    fragment RowOfficeStatsOutputFragment on RowOfficeStatsOutput {
        statsFrom
        statsTo
        totalLectureTerms
        totalReservations
        visitStats
        totalLectureRevenue {
            ...StatsRevenueFragment
        }
        totalPassRevenue {
            ...StatsRevenueFragment
        }
        totalCreditsRevenue {
            ...StatsRevenueFragment
        }
    }
    ${statsRevenueFragment}
    ${statsPenaltyStornoRevenueFragment}
`;

export const teacherLectureStatsFragment = gql`
    fragment TeacherLectureStatsFragment on TeacherLectureStatsOutput {
        id
        visitStats
        name
        lectureTermId
        start
        end
        reservationCount
        capacity
        lectureIsFree
        payments {
            ...StatsPenaltyStornoRevenueFragment
        }
        notPaidPayments {
            ...StatsPenaltyStornoRevenueFragment
        }
        stornoPayments {
            ...StatsPenaltyStornoRevenueFragment
        }
    }
    ${statsPenaltyStornoRevenueFragment}
`;

export const teacherStatsFragment = gql`
    fragment TeacherStatsFragment on TeacherStatsOutput {
        id
        name
        lectures {
            ...TeacherLectureStatsFragment
        }
    }
    ${teacherLectureStatsFragment}
`;

export const officeConfig = gql`
    fragment OfficeConfigFragment on OfficeConfigOutput {
        id
        officeConfigId
        openingHours
        closingTime
        freeCourseSpotsBookAfterCourseAllowed
        freeCourseSpotsBookBeforeDays
        openReservationBeforeAllowedWhenever
        openReservationBeforeMinutes
        openReservationBeforeDays
        openReservationBeforeHours
        closeReservationBeforeAllowedWhenever
        closeReservationBeforeMinutes
        closeReservationBeforeHours
        closeLogoutBeforeAllowedWhenever
        closeLogoutBeforeMinutes
        closeLogoutBeforeHours
        alternatesAllowed
        penaltyZonesAllowed
        moreLectureReservationsAllowed
        moreLectureReservationsCount
        reservationWithoutRegistrationAllowed
        doNotShowAttendeesForLecturesAndCourses
        doNotShowAttendeesForCourses
        cashPaymentForReservationEnabled
        timezone
        colorSchemePrimaryColor
        stripeConnectAccountId
        publicCalendarView
        dynamicOpenClassRegistrationActive
        dynamicCourseRegistrationActive
        dynamicWorkshopRegistrationActive
        dynamicNewClientActive
        dynamicOpenClassRegistrationConfig
        dynamicCourseRegistrationConfig
        dynamicWorkshopRegistrationConfig
        dynamicNewClientConfig
        courseOptOutBeforeMinutesAllowed
        courseOptOutBeforeMinutes
        courseOptOutValidityDays
        shouldIssueReceipts
        shouldGenerateReceiptsCustomTitle
        shouldGenerateReceiptsCustomText
        publicCalendarPrimaryColor
        publicCalendarDefaultView
        hideDashboardStats
        promoCodesVisibilityEnabled
        vouchersVisibilityEnabled
        lectorsCanSeeContacts
    }
`;

export const officeEntryPass = gql`
    fragment OfficeEntryPassFragment on OfficeEntryPassOutput {
        id
        passEntryId
        numberOfEntries
        price
        currency
        validityTimeValue
        validityTimeUnit
        description
        archived
        officeId
        expiringEarlyReminderSent
    }
`;

export const officeNotificationConfig = gql`
    fragment OfficeNotificationConfigFragment on OfficeNotificationConfigOutput {
        id
        configId
        studioClientCanceledReservationSms
        studioClientCanceledReservationEmail
        studioClientCreatedReservationSms
        studioClientCreatedReservationEmail
        studioClientBoughtEntryEmail
        employeeAssignedLectureRememberEmail
        clientReservationReminderEmail
        clientOnlineReservationReminderEmail
        clientOnlineReservationReminderEmailMinutesBefore
        clientReservationReminderEmailMinutesBefore
        clientReservationReminderSmsMinutesBefore
        teacherLectureReminderEmailMinutesBefore
        studioToClientLectureCanceledSms
        studioToClientAlternateRegisteredSms
        studioToClientLectureReminderSms
        studioToClientTermDateChangedSms
        usedSmsService
        officeId
        customFirstBookingMessage
        smsBalance
        studioAdminClientCreatedReservationEmail
        studioAdminClientCanceledReservationEmail
    }
`;

export const officeSmartemailingIntegration = gql`
    fragment OfficeSmartemailingIntegrationFragment on OfficeSmartemailingIntegrationOutput {
        id
        officeSmartemailingIntegrationId
        isActive
        email
        password
        officeId
    }
`;

export const officeMailerLiteIntegration = gql`
    fragment OfficeMailerLiteIntegrationFragment on OfficeMailerLiteIntegrationOutput {
        id
        officeMailerliteIntegrationId
        isActive
        apiKey
        officeId
    }
`;

export const officeEcomailIntegration = gql`
    fragment OfficeEcomailIntegrationFragment on OfficeEcomailIntegrationOutput {
        id
        officeEcomailIntegrationId
        isActive
        apiKey
        fromName
        fromEmail
        replyTo
        officeId
    }
`;

export const officeMailchimpIntegration = gql`
    fragment OfficeMailchimpIntegrationFragment on OfficeMailchimpIntegrationOutput {
        id
        officeMailchimpIntegrationId
        isActive
        contactListId
        contactWebId
        officeId
    }
`;

export const officeAllIntegrations = gql`
    fragment OfficeAllIntegrationsFragment on OfficeAllIntegrationsOutput {
        mailchimp {
            ...OfficeMailchimpIntegrationFragment
        }
        smartemailing {
            ...OfficeSmartemailingIntegrationFragment
        }
        ecomail {
            ...OfficeEcomailIntegrationFragment
        }
        mailerlite {
            ...OfficeMailerLiteIntegrationFragment
        }
    }
    ${officeMailchimpIntegration}
    ${officeSmartemailingIntegration}
    ${officeEcomailIntegration}
    ${officeMailerLiteIntegration}
`;

export const officeNotificationMessages = gql`
    fragment OfficeNotificationMessagesFragment on OfficeNotificationMessageOutput {
        id
        messagesId
        studioClientCanceledReservationSms
        studioClientCanceledReservationEmail
        studioClientCreatedReservationSms
        studioClientCreatedReservationEmail
        studioClientBoughtEntryEmail
        employeeAssignedLectureRememberEmail
        clientReservationReminderEmail
        clientOnlineReservationReminderEmail
    }
`;

export const officeEntryCredit = gql`
    fragment OfficeEntryCreditFragment on OfficeEntryCreditOutput {
        id
        creditEntryId
        numberOfCredits
        price
        currency
        validityTimeValue
        validityTimeUnit
        description
        archived
        officeId
        expiringEarlyReminderSent
    }
`;

export const officeLectureTemplate = gql`
    fragment OfficeLectureTemplateFragment on OfficeLectureTemplateOutput {
        id
        lectureTemplateId
        templateName
        lectureName
        image
        onlineLectureDescription
        videoLinkUrl
        videoLinkPassword
        color
        durationInMinutes
        capacity
        onlineCapacity
        description
        difficulty
        online
        physical
        onlineLectureInstancesSharedVideoLink
        lectureTemplateIsFree
        pricingOptions {
            ...PricingOptionFragment
        }
        paymentMethods {
            ...InstancePaymentMethodFragment
        }
        labels {
            ...LectureLabelOutputFragment
        }
    }
    ${pricingOption}
    ${instancePaymentMethod}
    ${organisationLabelFragment}
`;

export const cancellationPolicy = gql`
    fragment CancellationPolicyFragment on OfficeCancellationPolicyOutput {
        id
        cancellationPolicyTemplateId
        percent
        timeValue
        timeUnit
        officeId
    }
`;

const officeEmployeeBasic = gql`
    fragment OfficeEmployeeBasicFragment on OfficeEmployeeOutput {
        id
        employeeId
        orgDescription
        orgImage
        role
        productTourCompleted
        isActive
        isOrgDescriptionUsed
        isVisiblePublic
        gdprConsent
    }
`;

export const officeEmployee = gql`
    fragment OfficeEmployeeFragment on OfficeEmployeeOutput {
        ...OfficeEmployeeBasicFragment
        user {
            ...UserFragment
        }
    }
    ${officeEmployeeBasic}
    ${user}
`;

export const publicOfficeEmployee = gql`
    fragment PublicOfficeEmployeeFragment on OfficeEmployeeOutput {
        ...OfficeEmployeeBasicFragment
        user {
            id
            userId
            name
            description
            firstname
            lastname
            image
            registrationStatus
            email
            phone
            lastLogin
        }
    }
    ${officeEmployeeBasic}
`;

export const userEmployee = gql`
    fragment EmployeeFragment on OrganisationEmployeeOutput {
        id
        userId
        organisationEmployeeId
        organisationId
        role
        organisationOffices {
            id
            officeId
            organisationId
            organisationEmployeeId
            name
            domain
            description
            phone
            email
            url
            instagram
            facebookUrl
            youtubeUrl
            image
            role
            productTourCompleted
            defaultCurrencyCode
            officeConfig {
                timezone
                publicCalendarView
                hideDashboardStats
                alternatesAllowed
                closeLogoutBeforeAllowedWhenever
                closeLogoutBeforeHours
                penaltyZonesAllowed
                cashPaymentForReservationEnabled
                stripeConnectAccountId
                doNotShowAttendeesForLecturesAndCourses
                doNotShowAttendeesForCourses
                promoCodesVisibilityEnabled
                vouchersVisibilityEnabled
                lectorsCanSeeContacts
            }
            orgTariffMeta {
                ...OrganisationTariffMetaFragment
            }
            otherCurrencies {
                ...OfficeOtherCurrencyOutputFragment
            }
            tariffLimits {
                ...OrganisationOfficeTariffLimitsFragment
            }
            isExpertAlaUltimate
        }
        organisation {
            id
            organisationId
            name
            organisationType
            logo
            organisationTariff {
                ...OrganisationTariffFragment
            }
        }
    }
    ${organisationTariffMeta}
    ${officeOtherCurrency}
    ${organisationTariff}
    ${officeTariffLimits}
`;

export const userOrganisationClient = gql`
    fragment OrganisationClientFragment on OrganisationClientOutput {
        id
        userId
        organisationClientId
        organisationId
        organisationOffices {
            id
            officeId
            organisationId
            name
            domain
            url
            instagram
            facebookUrl
            youtubeUrl
            image
            officeConfig {
                timezone
                publicCalendarView
                alternatesAllowed
                closeLogoutBeforeAllowedWhenever
                closeLogoutBeforeHours
                penaltyZonesAllowed
                cashPaymentForReservationEnabled
                stripeConnectAccountId
                doNotShowAttendeesForLecturesAndCourses
                doNotShowAttendeesForCourses
                promoCodesVisibilityEnabled
                vouchersVisibilityEnabled
            }
            orgTariffMeta {
                ...OrganisationTariffMetaFragment
            }
            otherCurrencies {
                ...OfficeOtherCurrencyOutputFragment
            }
            tariffLimits {
                ...OrganisationOfficeTariffLimitsFragment
            }
            isExpertAlaUltimate
        }
        organisation {
            id
            organisationId
            name
            organisationType
            logo
        }
    }
    ${organisationTariffMeta}
    ${officeOtherCurrency}
    ${officeTariffLimits}
`;

export const orderOutput = gql`
    fragment OrderOutputFragment on OrderOutput {
        clientOrder {
            ...ClientOrderFragment
        }
    }
    ${clientOrder}
`;

export const addCreditPass = gql`
    fragment AddCreditPassEntryOutputFragment on AddCreditPassEntryOutput {
        paymentStatus
        bankAccountPayment {
            bankAccount
            variableSymbol
        }
    }
`;

export const userConfig = gql`
    fragment ConfigFragment on UserConfigOutput {
        id
        userConfigId
        lang
        gdprConsent
        marketingConsent
    }
`;

export const lectureType = gql`
    fragment LectureTypeFragment on LectureTypeOutput {
        id
        lectureTypeId
        organisationId
        color
        name
        description
    }
`;

export const lectureInstance = gql`
    fragment LectureInstanceFragment on LectureInstanceOutput {
        id
        lectureId
        lectureName
        invoiceDescription
        color
        image
        durationInMinutes
        capacity
        onlineCapacity
        description
        difficulty
        afterCourseStart
        online
        physical
        onlineLectureInstancesSharedVideoLink
        status
        private
        frequencyType
        isCourseAndIsCanceled
        type
        pricingOptions {
            ...PricingOptionFragment
        }
        paymentMethods {
            ...InstancePaymentMethodFragment
        }
        forbiddenToBook
        payWithMakeUpSession
        lectureIsFree
        labels {
            ...LectureLabelOutputFragment
        }
    }
    ${pricingOption}
    ${instancePaymentMethod}
    ${organisationLabelFragment}
`;

export const lectureTerm = gql`
    fragment LectureTermFragment on LectureTermOutput {
        id
        lectureTermId
        start
        end
        onlineLectureDescription
        courseDescription
        videoLinkUrl
        videoLinkPassword
        officeId
        isCanceled
        passed
        visibleInPublicCalendar
        activeReservationsCount
        courseUpdatedPlace {
            ...OfficePlaceFragment
        }
        courseUpdatedLecturer {
            ...OfficeEmployeeFragment
        }
        lectureInstance {
            ...LectureInstanceFragment
        }
    }
    ${officePlace}
    ${officeEmployee}
    ${lectureInstance}
`;

export const futureLectureTerm = gql`
    fragment FutureLectureTermFragment on FutureLectureTermOutput {
        id
        lectureTermId
        start
        end
        onlineLectureDescription
        courseDescription
        videoLinkUrl
        videoLinkPassword
        officeId
        isCanceled
        passed
        visibleInPublicCalendar
        reservations {
            ...UserReservationPublicFragment
        }
    }
    ${userReservationsPublic}
`;

export const allCourseTerms = gql`
    fragment AllCourseTermsFragment on AllCourseTermOutput {
        id
        lectureTermId
        start
        end
        onlineLectureDescription
        courseDescription
        videoLinkUrl
        videoLinkPassword
        officeId
        isCanceled
        passed
        visibleInPublicCalendar
    }
`;

export const onlineLectureInfo = gql`
    fragment OnlineLectureInfoFragment on LectureTermOutput {
        onlineLectureDescription
        videoLinkUrl
        videoLinkPassword
    }
`;

export const confirmFragment = gql`
    fragment ConfirmFragment on ConfirmOutput {
        success
    }
`;

export const workshopPaymentMethod = gql`
    fragment WorkshopPaymentMethodFragment on WorkshopPaymentMethodOutput {
        id
        workshopPaymentMethodId
        isActive
        workshopId
        officePaymentMethodId
        officeId
        officePaymentMethod {
            ...OfficePaymentMethodFragment
        }
    }
    ${officePaymentMethod}
`;

export const workshop = gql`
    fragment WorkshopFragment on WorkshopOutput {
        id
        workshopId
        officeId
        name
        start
        end
        shortDescription
        longDescription
        street
        city
        postalCode
        country
        placePhoto
        capacity
        doNotShowAttendees
        isCanceled
        workshopHeaderImage
        visibleInPublicCalendar
        pricingOptions {
            id
            pricingOptionId
            value
            currency
            type
            name
            isVisible
        }
        workshopImages
        lecturers {
            ...PublicOfficeEmployeeFragment
        }
        paymentMethods {
            ...WorkshopPaymentMethodFragment
        }
        labels {
            ...LectureLabelOutputFragment
        }
    }
    ${publicOfficeEmployee}
    ${workshopPaymentMethod}
    ${organisationLabelFragment}
`;

export const simpleWorkshop = gql`
    fragment WorkshopSimpleFragment on WorkshopOutput {
        id
        workshopId
        officeId
        name
        start
        end
        shortDescription
        longDescription
        street
        city
        postalCode
        country
        placePhoto
        capacity
        doNotShowAttendees
        isCanceled
        workshopHeaderImage
        visibleInPublicCalendar
    }
`;

export const currency = gql`
    fragment CurrencyFragment on CurrencyOutput {
        code
    }
`;

export const notRegisteredUserReservation = gql`
    fragment NotRegisteredUserReservationFragment on ReservationOutput {
        id
        reservationId
        reservationToken
        clientOrder {
            ...ClientOrderFragment
        }
        type
    }
    ${clientOrder}
`;

export const registeredUserReservation = gql`
    fragment RegisteredUserReservationFragment on ReservationOutput {
        id
        reservationId
        reservationToken
        clientOrder {
            ...ClientOrderFragment
        }
        type
    }
    ${clientOrder}
`;

export const publicCourse = gql`
    fragment PublicCourseFragment on CourseOutput {
        id
        lectureId
        lectureName
        color
        image
        durationInMinutes
        forbiddenToBook
        payWithMakeUpSession
        capacity
        onlineCapacity
        description
        difficulty
        afterCourseStart
        online
        physical
        onlineLectureInstancesSharedVideoLink
        status
        private
        frequencyType
        isCourseAndIsCanceled
        type
        totalActiveReservationsCount
        totalWholeCourseReservationsCount
        place {
            ...OfficePlaceFragment
            address {
                ...AddressFragment
            }
        }
        lecturer {
            ...OfficeEmployeeFragment
        }
        pricingOptions {
            ...PricingOptionFragment
        }
        terms {
            ...LectureTermFragment
        }
        paymentMethods {
            ...InstancePaymentMethodFragment
        }
        labels {
            ...LectureLabelOutputFragment
        }
    }
    ${officePlace}
    ${address}
    ${officeEmployee}
    ${pricingOption}
    ${lectureTerm}
    ${instancePaymentMethod}
    ${organisationLabelFragment}
`;

export const publicCourseOverviewWithoutTerms = gql`
    fragment PublicCourseOverviewWithoutTermsFragment on CourseOverviewWithoutTermsOutput {
        id
        lectureId
        lectureName
        color
        image
        durationInMinutes
        forbiddenToBook
        invoiceDescription
        capacity
        onlineCapacity
        description
        difficulty
        afterCourseStart
        online
        physical
        onlineLectureInstancesSharedVideoLink
        status
        private
        frequencyType
        type
        totalActiveReservationsCount
        totalWholeCourseReservationsCount
        isCanceled
        isOngoingCourse
        onlyOneTermNotPassed
        start
        end
        startTerm {
            ...LectureTermFragment
        }
        endTerm {
            ...LectureTermFragment
        }
        notCanceledTermsCount
        place {
            ...OfficePlaceFragment
            address {
                ...AddressFragment
            }
        }
        lecturer {
            ...OfficeEmployeeFragment
        }
        pricingOptions {
            ...PricingOptionFragment
        }
        paymentMethods {
            ...InstancePaymentMethodFragment
        }
        labels {
            ...LectureLabelOutputFragment
        }
    }
    ${lectureTerm}
    ${officePlace}
    ${address}
    ${officeEmployee}
    ${pricingOption}
    ${instancePaymentMethod}
    ${organisationLabelFragment}
`;

export const privateCourse = gql`
    fragment PrivateCourseFragment on CourseOutput {
        id
        lectureId
        lectureName
        color
        image
        durationInMinutes
        forbiddenToBook
        payWithMakeUpSession
        capacity
        onlineCapacity
        description
        difficulty
        afterCourseStart
        online
        physical
        onlineLectureInstancesSharedVideoLink
        status
        private
        frequencyType
        isCourseAndIsCanceled
        type
        totalActiveReservationsCount
        totalWholeCourseReservationsCount
        place {
            ...OfficePlaceFragment
            address {
                ...AddressFragment
            }
        }
        clientOrders {
            ...FullOfficeClientOrderFragment
        }
        lecturer {
            ...OfficeEmployeeFragment
        }
        terms {
            ...LectureTermFragment
        }
        paymentMethods {
            ...InstancePaymentMethodFragment
        }
        labels {
            ...LectureLabelOutputFragment
        }
    }
    ${officePlace}
    ${address}
    ${fullOfficeClientOrder}
    ${officeEmployee}
    ${lectureTerm}
    ${instancePaymentMethod}
    ${organisationLabelFragment}
`;

export const promoCodeBase = gql`
    fragment PromoCodeBaseFragment on PromoCodeOutput {
        id
        promoCodeId
        name
        validTo
        validFrom
        strategy
        value
        currency
        percents
        isForLectures
        isForWorkshops
        isForCourses
        isForBundles
    }
`;

export const promoCodeOffice = gql`
    fragment PromoCodeOfficeFragment on PromoCodeOutput {
        ...PromoCodeBaseFragment
        code
        usedTimes
        isForLectures
        isForWorkshops
        isForCourses
        isForBundles
    }
    ${promoCodeBase}
`;
export const promoCodePublic = gql`
    fragment PromoCodePublicFragment on PromoCodeOutput {
        ...PromoCodeBaseFragment
        officeId
    }
    ${promoCodeBase}
`;

export const officeVoucher = gql`
    fragment OfficeVoucherFragment on OfficeVoucherOutput {
        id
        officeVoucherId
        name
        value
        currency
        validityDays
        image
    }
`;
